import { Component, EventEmitter, forwardRef, Inject, Input, OnInit, Output, ViewChild } from "@angular/core";
import { NgForm } from "@angular/forms";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { firstValueFrom } from "rxjs";
import { Permissions } from "../../../common/constants";
import { DefaultPlugin, EventTopic, Location, NotificationSettings, User } from "../../../model";
import { AlertDefinitionService } from "../../../service/alert-definition.service";
import { AuthenticationService } from "../../../service/authentication.service";
import { UserLocationService } from "../../../service/user-location.service";
import { UserService } from "../../users/user.service";
import { LocationSelectionDialogComponent } from "../location-selection-dialog/location-selection-dialog.component";

@Component({
    selector: 'profile-notifications-v2',
    template: require('./profile-notifications-v2.component.html'),
    styles: [require('./profile-notifications-v2.component.css')]
})
export class ProfileNotificationsV2Component implements OnInit {

    @Input() user: User;

    @Output() saveAction = new EventEmitter<void>();

    @ViewChild('form') form: NgForm;

    userNotificationSettings: NotificationSettings;
    locationsMap: { [id: string]: Location } = {};
    topics: EventTopic[];
    selectedLocationIds: string[];
    alertPermissions: boolean;
    workSessionPermissions: boolean;
    ordersPermissions: boolean;
    showPush: boolean;
    showVoice: boolean;
    showSms: boolean;
    writePermission: boolean;

    private isSameUser: boolean;
    private endpointUserId: string;

    constructor(
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => UserService)) private userService: UserService,
        @Inject(forwardRef(() => AlertDefinitionService)) private alertDefinitionService: AlertDefinitionService,
        @Inject(forwardRef(() => UserLocationService)) private userLocationService: UserLocationService,
        @Inject(forwardRef(() => MatDialog)) private dialog: MatDialog
    ) { }

    ngOnInit(): void {
        this.isSameUser = this.authenticationService.getUser().id == this.user.id;
        this.endpointUserId = this.isSameUser ? 'me' : this.user.id;
        this.initPermissions();
        this.initNotificationSettings();
        this.initLocations();
        this.initTopics();
    }

    private initPermissions(): void {
        this.writePermission = this.isSameUser || this.authenticationService.hasPermission(Permissions.WRITE_USER_NOTIFICATION_SETTINGS);
        this.alertPermissions = this.user.permissions?.includes(Permissions.RECEIVE_THING_ALERT_NOTIFICATIONS);
        this.workSessionPermissions = this.user.permissions?.includes(Permissions.RECEIVE_THING_WORK_SESSION_NOTIFICATIONS) && !!(this.user.customerId || this.user.locationId);
        this.ordersPermissions = this.user.permissions?.includes(Permissions.READ_ORDER);
        const enabledPlugins = this.authenticationService.getEnabledPlugins();
        this.showPush = enabledPlugins.some(p => p.name == DefaultPlugin.ANDROID_PUSH || p.name == DefaultPlugin.IOS_PUSH);
        this.showVoice = enabledPlugins.some(p => p.name == DefaultPlugin.INFOBIP && p.properties?.voiceEnabled == 'true');
        this.showSms = enabledPlugins.some(p => p.name == DefaultPlugin.SMS) || enabledPlugins.some(p => p.name == DefaultPlugin.INFOBIP && p.properties?.smsEnabled == 'true');
    }

    private initNotificationSettings(): void {
        this.userService.getNotificationSettings(this.endpointUserId).then(userNotificationSettings => {
            this.userNotificationSettings = userNotificationSettings;
            this.selectedLocationIds = userNotificationSettings.locationIds || [];
        });
    }

    private initLocations(): void {
        this.userLocationService.getRecursivelyAllLocations().then(locations => {
            locations.forEach(l => this.locationsMap[l.id] = l);
        });
    }

    private initTopics(): void {
        this.alertDefinitionService.getEventTopics().then(topics => this.topics = topics);
    }

    openSelectLocationsDialog(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '400px';
        dialogConfig.autoFocus = false;
        dialogConfig.data = {
            locations: Object.values(this.locationsMap).filter(l => !this.selectedLocationIds.includes(l.id))
        };
        firstValueFrom(this.dialog.open(LocationSelectionDialogComponent, dialogConfig).afterClosed()).then(selectedLocationIds => {
            if (selectedLocationIds) {
                this.selectedLocationIds.push(...selectedLocationIds);
            }
        });
    }

    removeLocation(index: number): void {
        this.selectedLocationIds.splice(index, 1);
    }

    save(): void {
        this.userService.updateNotificationSettings(this.endpointUserId, this.getBody()).then(() => this.saveAction.emit())
    }

    private getBody(): any {
        let body = this.form.form.getRawValue();
        if (body.allTopics) {
            body.topics = null;
        } else {
            body.topics = Object.keys(body).filter(k => k.startsWith('topic_') && body[k]).map(k => k.substring(6));
        }
        if (body.allLocations) {
            body.locationIds = null;
        } else {
            body.locationIds = this.selectedLocationIds;
        }
        return body;
    }
}